import React, { useState, useEffect, useCallback, useRef } from "react";
import './Wishlist.scss';
import { LoadingStateCard } from "../../Components/LoadingStateCard/LoadingStateCard";
import OverlayLoader from "../../Components/Loader/overlay_loader";
import returnIcon from "../../Cart/Assets/returnIcon.svg";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { isFullyOutOfStockFromSizeMap, totalStockFromSizeMap } from "../../utils/inventoryFromSizeMap";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../Contexts/ProfileContext";
import { useToast } from "../../Contexts/ToastContext";
import { saveCheckoutDraftForBuyNow } from "../../utils/checkoutDraft";
import { IoCheckmark } from "react-icons/io5";

const MIN_CART_LOADER_MS = 400;

/** Matches mob orders list pagination pattern — items per request. */
const WISHLIST_PAGE_SIZE = 3;

/** Normalize GET /products/wish-list body: supports legacy plain array or { items, total }. */
function normalizeWishlistResponse(data) {
    if (data == null) return { items: [], total: 0 };
    if (Array.isArray(data)) return { items: data, total: data.length };
    const items =
        data.items ?? data.wishlist ?? data.data ?? data.results ?? data.rows;
    const total =
        data.total ??
        data.totalCount ??
        data.count ??
        (Array.isArray(items) ? items.length : 0);
    return {
        items: Array.isArray(items) ? items : [],
        total: Number(total) || 0,
    };
}

function dedupeAppendById(prev, chunk) {
    const seen = new Set(
        prev.map((x) => String(x._id ?? x.id ?? x.product_id ?? ""))
    );
    const out = [...prev];
    for (const row of chunk) {
        const k = String(row._id ?? row.id ?? row.product_id ?? "");
        if (k && !seen.has(k)) {
            seen.add(k);
            out.push(row);
        }
    }
    return out;
}

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

/** Sizes to show in picker; `available` false when stock is 0. */
function getWishlistSizeOptions(item) {
    const sizeMap = item.size && typeof item.size === 'object' && !Array.isArray(item.size) ? item.size : null;
    let keys;
    if (sizeMap && Object.keys(sizeMap).length > 0) {
        keys = Object.keys(sizeMap);
    } else {
        keys = Array.isArray(item.availableSizes) && item.availableSizes.length > 0
            ? [...item.availableSizes]
            : ['S', 'M', 'L', 'XL'];
    }
    const uniq = [...new Set(keys.map(String))];
    uniq.sort((a, b) => {
        const ua = a.toUpperCase();
        const ub = b.toUpperCase();
        const ia = SIZE_ORDER.indexOf(ua);
        const ib = SIZE_ORDER.indexOf(ub);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        return a.localeCompare(b);
    });
    return uniq.map((key) => ({
        key,
        available: sizeMap ? Number(sizeMap[key]) > 0 : true,
    }));
}

function pickDefaultSize(options, preferred) {
    if (!options.length) return null;
    const pref = preferred != null ? String(preferred) : null;
    const match = options.find((o) => o.key === pref && o.available);
    if (match) return match.key;
    const firstAv = options.find((o) => o.available);
    return firstAv ? firstAv.key : options[0].key;
}

function guestCartProductIdSet() {
    try {
        const raw = localStorage.getItem("guestCart");
        const arr = raw ? JSON.parse(raw) : [];
        const set = {};
        (arr || []).forEach((c) => {
            if (c.product_id != null) set[String(c.product_id)] = true;
        });
        return set;
    } catch {
        return {};
    }
}

export function MobWishlist() {
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSizes, setSelectedSizes] = useState({});
    const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(null);
    /** Product id while add-to-cart runs (button-level loader only) */
    const [addingToCartPid, setAddingToCartPid] = useState(null);
    /** Full-page overlay while buy-now checkout is prepared */
    const [buyNowPageLoading, setBuyNowPageLoading] = useState(false);
    const [cartAddedByProductId, setCartAddedByProductId] = useState({});
    /** { item, index, intent: 'cart' | 'buynow' } | null */
    const [sizePicker, setSizePicker] = useState(null);
    const [pendingSize, setPendingSize] = useState(null);

    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [noMoreItems, setNoMoreItems] = useState(false);
    const [startRef, setStartRef] = useState(false);
    const [listReady, setListReady] = useState(false);
    const guestFullListRef = useRef([]);
    const initialFetchDoneRef = useRef(false);
    const observerRef = useRef(null);
    const fetchingMoreRef = useRef(false);

    const syncGuestCartAddedFlags = useCallback(() => {
        if (localStorage.getItem("guestUser") !== "true") return;
        const fromCart = guestCartProductIdSet();
        setCartAddedByProductId((prev) => ({ ...prev, ...fromCart }));
    }, []);

    useEffect(() => {
        syncGuestCartAddedFlags();
    }, [wishlistItems, syncGuestCartAddedFlags]);

    useEffect(() => {
        window.addEventListener("guestCartUpdated", syncGuestCartAddedFlags);
        return () => window.removeEventListener("guestCartUpdated", syncGuestCartAddedFlags);
    }, [syncGuestCartAddedFlags]);

    const mapGuestWishlistRow = (item) => {
        const price = item.price || 0;
        const mrp = item.mrp || price;
        const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
        let imageUrl = null;
        if (item.urls && Array.isArray(item.urls) && item.urls.length > 0) {
            imageUrl = item.urls[1] || item.urls[0];
        }
        const productSizes =
            item.size && typeof item.size === "object" && !Array.isArray(item.size) ? item.size : {};
        const availableSizesForProduct = Object.keys(productSizes).filter(
            (size) => Number(productSizes[size]) > 0
        );
        const selectedSize =
            item.selectedSize && Number(productSizes[item.selectedSize]) > 0
                ? item.selectedSize
                : availableSizesForProduct[0] || item.selectedSize || "M";
        const inStock = !isFullyOutOfStockFromSizeMap(productSizes);
        const stockCount = inStock
            ? Object.keys(productSizes).length > 0
                ? totalStockFromSizeMap(productSizes)
                : 10
            : 0;
        return {
            id: item._id,
            _id: item._id,
            product_id: item.product_id,
            image: imageUrl,
            brand: item.brand || "Brand",
            title: item.title || "Product",
            seller: "Seller",
            price,
            mrp,
            discount,
            selectedSize,
            availableSizes:
                availableSizesForProduct.length > 0
                    ? availableSizesForProduct
                    : ["S", "M", "L", "XL"],
            size: productSizes,
            rating: 4.0,
            reviews: 0,
            urls: item.urls || [],
            inStock,
            stockCount,
        };
    };

    const mapApiWishlistRow = (item, index) => {
        const product = item.product_data || item.product || item;
        const productSizes = product.size || {};
        const availableSizesForProduct = Object.keys(productSizes).filter(
            (size) => Number(productSizes[size]) > 0
        );
        const selectedSize =
            item.selectedSize && Number(productSizes[item.selectedSize]) > 0
                ? item.selectedSize
                : availableSizesForProduct[0] || item.selectedSize || "M";
        const inStock = !isFullyOutOfStockFromSizeMap(productSizes);
        const stockCount = inStock
            ? Object.keys(productSizes).length > 0
                ? totalStockFromSizeMap(productSizes)
                : 10
            : 0;
        const price = product.price || 0;
        const mrp = product.mrp || price;
        const discount =
            mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
        let imageUrl = null;
        const urls = product.urls || item.urls;
        if (urls) {
            const raw =
                Array.isArray(urls) && urls.length > 0
                    ? urls[1] || urls[0]
                    : typeof urls === "string"
                      ? urls
                      : null;
            if (raw) {
                imageUrl = String(raw).startsWith("http")
                    ? raw
                    : `${API_BASE_URL}/${raw.replace(/^\//, "")}`;
            }
        }
        return {
            id: item._id || item.id || index + 1,
            _id: item._id || item.id,
            product_id: item.product_id || product._id,
            image: imageUrl,
            brand: product.brand?.[0] || product.brand || "Brand",
            title: product.title || "Product",
            seller: product.seller || "Seller",
            price,
            mrp,
            discount,
            selectedSize,
            availableSizes:
                availableSizesForProduct.length > 0
                    ? availableSizesForProduct
                    : ["S", "M", "L", "XL"],
            rating: product.rating || 4.0,
            reviews: product.reviews || 0,
            size: productSizes,
            inStock,
            stockCount,
        };
    };

    const applySizesForRows = (rows) => {
        const sizesObj = {};
        rows.forEach((item, index) => {
            sizesObj[index] = item.selectedSize;
        });
        setSelectedSizes(sizesObj);
    };

    /**
     * @param {number} requestedPage 1-based
     * @param {{ append?: boolean }} opts append ignored for guest (uses slice)
     */
    const fetchWishlistPage = async (requestedPage) => {
        const isGuest = localStorage.getItem("guestUser") === "true";

        if (isGuest) {
            try {
                if (requestedPage > 1) {
                    if (fetchingMoreRef.current) return;
                    fetchingMoreRef.current = true;
                }
                if (requestedPage === 1) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }
                const guestWishlist = JSON.parse(
                    localStorage.getItem("guestWishlist") || "[]"
                );
                const transformedAll = guestWishlist.map(mapGuestWishlistRow);
                guestFullListRef.current = transformedAll;
                const end = Math.min(
                    transformedAll.length,
                    requestedPage * WISHLIST_PAGE_SIZE
                );
                const slice = transformedAll.slice(0, end);
                setWishlistItems(slice);
                applySizesForRows(slice);
                setNoMoreItems(end >= transformedAll.length);
            } catch (e) {
                console.error(e);
                setWishlistItems([]);
            } finally {
                fetchingMoreRef.current = false;
                setLoading(false);
                setLoadingMore(false);
                setListReady(true);
                setStartRef(true);
            }
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
            console.warn("No token found");
            setWishlistItems([]);
            setLoading(false);
            setListReady(true);
            setStartRef(true);
            setNoMoreItems(true);
            return;
        }

        if (requestedPage > 1) {
            if (fetchingMoreRef.current) return;
            fetchingMoreRef.current = true;
        }

        if (requestedPage === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    page: requestedPage,
                    size: WISHLIST_PAGE_SIZE,
                },
            };

            const response = await axios.get(`/products/wish-list`, config);
            const { items: rawItems } = normalizeWishlistResponse(response.data);
            const transformedData = rawItems.map(mapApiWishlistRow);

            if (requestedPage === 1) {
                setWishlistItems(transformedData);
                applySizesForRows(transformedData);
            } else {
                let merged;
                setWishlistItems((prev) => {
                    merged = dedupeAppendById(prev, transformedData);
                    return merged;
                });
                if (merged) {
                    applySizesForRows(merged);
                }
            }

            if (transformedData.length < WISHLIST_PAGE_SIZE) {
                setNoMoreItems(true);
            }
        } catch (error) {
            console.error("Error fetching wishlist:", error);
            if (requestedPage === 1) {
                setWishlistItems([]);
            }
        } finally {
            fetchingMoreRef.current = false;
            setLoading(false);
            setLoadingMore(false);
            setListReady(true);
            setStartRef(true);
        }
    };

    useEffect(() => {
        if (initialFetchDoneRef.current) return;
        initialFetchDoneRef.current = true;
        setPage(1);
        setWishlistItems([]);
        setNoMoreItems(false);
        setStartRef(false);
        setListReady(false);
        fetchWishlistPage(1);
    }, []);

    useEffect(() => {
        if (page > 1 && startRef) {
            fetchWishlistPage(page);
        }
    }, [page, startRef]);

    const lastWishlistSentinelRef = useCallback(
        (node) => {
            if (loadingMore || noMoreItems || !startRef || !listReady) return;
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (!entries[0]?.isIntersecting) return;
                    if (fetchingMoreRef.current || loadingMore) return;
                    setPage((p) => p + 1);
                },
                { root: null, rootMargin: "0px 0px 120px 0px", threshold: 0 }
            );
            if (node) {
                observerRef.current.observe(node);
                const rect = node.getBoundingClientRect();
                if (rect.top < window.innerHeight && rect.top >= 0) {
                    setPage((p) => p + 1);
                }
            }
        },
        [loadingMore, noMoreItems, startRef, listReady]
    );

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!sizePicker) {
            setPendingSize(null);
            return;
        }
        const opts = getWishlistSizeOptions(sizePicker.item);
        const def = pickDefaultSize(opts, selectedSizes[sizePicker.index] ?? sizePicker.item.selectedSize);
        setPendingSize(def);
        // Intentionally only when the sheet opens — not when selectedSizes changes while open
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync default size at open
    }, [sizePicker]);

    const performAddToCart = async (item, index, size, intent = "cart") => {
        const productId = String(item.product_id || item._id);
        const startedAt = Date.now();
        const isBuyNow = intent === "buynow";

        if (!isBuyNow && cartAddedByProductId[productId]) {
            showToast({
                message: "Already in cart",
                action: { label: "View cart", onClick: () => navigate("/cart") },
            });
            return true;
        }

        if (!isBuyNow && addingToCartPid === productId) return false;

        try {
            if (!isBuyNow) {
                setAddingToCartPid(productId);
            }

            const isGuest = localStorage.getItem("guestUser") === "true";
            if (isGuest) {
                const guestCart = JSON.parse(localStorage.getItem("guestCart") || "[]");
                const rawPid = item.product_id || item._id;
                const existingIndex = guestCart.findIndex((c) => String(c.product_id) === String(rawPid));
                if (existingIndex >= 0) {
                    guestCart[existingIndex].selectedQty = (guestCart[existingIndex].selectedQty || 1) + 1;
                    if (isBuyNow) {
                        guestCart[existingIndex].selectedSize = size;
                    }
                } else {
                    guestCart.push({
                        _id: `guest-cart-${Date.now()}-${rawPid}`,
                        product_id: rawPid,
                        urls: item.urls || (item.image ? [item.image] : []),
                        title: item.title || "Product",
                        price: item.price,
                        brand: item.brand,
                        mrp: item.mrp,
                        selectedSize: size,
                        selectedQty: 1,
                        size: { [size]: 10 },
                        availableSizes: [size],
                        availableQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                        maxStock: 10,
                    });
                }
                localStorage.setItem("guestCart", JSON.stringify(guestCart));
                window.dispatchEvent(new Event("cartBadgePulse"));
                if (!isBuyNow) {
                    window.dispatchEvent(new CustomEvent("guestCartUpdated"));
                    setCartAddedByProductId((prev) => ({ ...prev, [productId]: true }));
                    showToast({
                        message: "Added to cart",
                        action: { label: "View cart", onClick: () => navigate("/cart") },
                    });
                }
                return true;
            }

            const token = localStorage.getItem("token");
            if (!token) {
                showToast({ message: "Please login to add items to cart" });
                return false;
            }

            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(
                `/cart/${item.product_id}`,
                { selectedSize: size, selectedQty: 1, pincode: "" },
                config
            );
            await fetchProfile();
            window.dispatchEvent(new Event("cartBadgePulse"));
            if (!isBuyNow) {
                setCartAddedByProductId((prev) => ({ ...prev, [productId]: true }));
                showToast({
                    message: "Added to cart",
                    action: { label: "View cart", onClick: () => navigate("/cart") },
                });
            }
            return true;
        } catch (error) {
            console.error("Error adding to cart:", error);
            showToast({
                message:
                    error.response?.data?.message || "Failed to add item to cart. Please try again.",
            });
            return false;
        } finally {
            if (!isBuyNow) {
                const elapsed = Date.now() - startedAt;
                const delay = Math.max(0, MIN_CART_LOADER_MS - elapsed);
                const clearLoading = () => setAddingToCartPid(null);
                if (delay > 0) {
                    setTimeout(clearLoading, delay);
                } else {
                    clearLoading();
                }
            }
        }
    };

    const openSizePickerIfNeeded = (item, index, intent) => {
        if (!item.inStock) return;
        const productId = String(item.product_id || item._id);
        if (cartAddedByProductId[productId]) {
            showToast({
                message: "Already in cart",
                action: { label: "View cart", onClick: () => navigate("/cart") },
            });
            return;
        }
        setSizePicker({ item, index, intent });
    };

    const confirmSizeSelection = async () => {
        if (!sizePicker || pendingSize == null) return;
        const opts = getWishlistSizeOptions(sizePicker.item);
        const sel = opts.find((o) => o.key === pendingSize);
        if (!sel?.available) return;
        const { item, index, intent } = sizePicker;
        const sizeToAdd = pendingSize;
        setSelectedSizes((prev) => ({ ...prev, [index]: sizeToAdd }));
        setSizePicker(null);
        if (intent === "buynow") {
            setBuyNowPageLoading(true);
            try {
                const ok = await performAddToCart(item, index, sizeToAdd, "buynow");
                if (ok) {
                    await saveCheckoutDraftForBuyNow(item, {
                        selectedSize: sizeToAdd,
                        selectedQty: 1,
                    });
                    navigate("/order-summary");
                }
            } catch (error) {
                console.error("Buy now checkout:", error);
                showToast({
                    message:
                        error.response?.data?.message ||
                        error.message ||
                        "Could not start checkout. Please try again.",
                });
            } finally {
                setBuyNowPageLoading(false);
            }
        } else {
            await performAddToCart(item, index, sizeToAdd, "cart");
        }
    };

    const handleBuyNow = (item, index) => {
        if (!item.inStock) return;
        openSizePickerIfNeeded(item, index, "buynow");
    };

    const handleRemoveFromWishlist = async (index) => {
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const item = wishlistItems[index];
                const wishlistId = item._id || item.id;
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const filtered = guestWishlist.filter((w) => String(w._id) !== String(wishlistId));
                localStorage.setItem('guestWishlist', JSON.stringify(filtered));
                setPage(1);
                setNoMoreItems(false);
                await fetchWishlistPage(1);
                setConfirmRemoveIndex(null);
                return;
            }

            const token = localStorage.getItem("token");
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const item = wishlistItems[index];
            const wishlistId = item._id || item.id;

            await axios.delete(
                `/products/wish-list/${wishlistId}`,
                config
            );

            setWishlistItems((prev) => {
                const next = prev.filter((_, i) => i !== index);
                applySizesForRows(next);
                return next;
            });
            await fetchProfile();
        } catch (error) {
            console.error('Error removing from wishlist:', error);
            showToast({ message: 'Failed to remove item from wishlist. Please try again.' });
        } finally {
            setConfirmRemoveIndex(null);
        }
    };

    const isEmpty =
        listReady && !loading && wishlistItems.length === 0;
    const sheetSizeOptions = sizePicker ? getWishlistSizeOptions(sizePicker.item) : [];
    const sheetHasStock = sheetSizeOptions.some((o) => o.available);
    const pendingOption = sheetSizeOptions.find((o) => o.key === pendingSize);

    if (loading) {
        return <LoadingStateCard message="Loading wishlist..." />;
    }

    if (isEmpty) {
        return (
            <>
                <div className="mob-base-cartLayout-mob wishlist-empty-state">
                    <div className="empty-cart-container">
                        <div className="empty-cart-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 6.00019C10.2006 3.90317 7.19377 3.2551 4.93923 5.17534C2.68468 7.09558 2.36727 10.3061 4.13778 12.5772C5.60984 14.4654 10.0648 18.4479 11.5249 19.7369C11.6882 19.8811 11.7699 19.9532 11.8652 19.9815C11.9483 20.0062 12.0393 20.0062 12.1225 19.9815C12.2178 19.9532 12.2994 19.8811 12.4628 19.7369C13.9229 18.4479 18.3778 14.4654 19.8499 12.5772C21.6204 10.3061 21.3417 7.07538 19.0484 5.17534C16.7551 3.2753 13.7994 3.90317 12 6.00019Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h2 className="empty-cart-title">No items in wishlist</h2>
                        <p className="empty-cart-message">Your wishlist is empty. Start shopping to add items you love!</p>
                        <button
                            className="empty-cart-button"
                            onClick={() => navigate('/')}
                        >
                            Continue Shopping
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="mob-base-wishlistLayout-mob">
                <div className="itemBlock-base-leftBlock">
                    <div className="cartItemsList">
                        {wishlistItems.map((item, index) => {
                                const rowProductId = String(item.product_id || item._id);
                                const isAddToCartLoading = addingToCartPid === rowProductId;
                                return (
                                    <div
                                        key={String(item._id ?? item.product_id ?? index)}
                                        className="itemContainer-base-itemMargin"
                                    >
                                        <div className={`itemContainer-base-item ${!item.inStock ? "itemContainer-base-item--oos" : ""}`}>
                                            <div
                                                className={`itemContainer-base-itemLeft ${!item.inStock ? "itemContainer-base-itemLeft--oos" : ""}`}
                                            >
                                                <img src={item.image || ""} alt={item.title} />
                                                {!item.inStock && (
                                                    <div className="wishlist-mob-oos-overlay" aria-hidden>
                                                        <span className="wishlist-mob-oos-badge">Out of stock</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="itemContainer-base-itemRight">
                                                <button 
                                                    className="cart-item-delete-btn"
                                                    onClick={() => setConfirmRemoveIndex(index)}
                                                    title="Remove from wishlist"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                                        <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </button>
                                                
                                                <div className="brand-and-seller">
                                                    <div>{item.brand}</div>
                                                    <a>{item.title}</a>
                                                </div>

                                                <div className="itemComponents-base-sellerContainer">
                                                    <div className="itemComponents-base-sellerData">
                                                        Sold by: {item.seller}
                                                    </div>
                                                </div>

                                                {/* Rating Container - Wishlist Specific */}
                                                <div className="rating-container">
                                                    <div className="overall-rating">
                                                        <div>{item.rating?.toFixed(1) || '4.0'}</div>
                                                        <span className="myntraweb-sprite"></span>
                                                        <div className="index-separator">|</div>
                                                        <div>{item.reviews || 0} Ratings</div>
                                                    </div>
                                                </div>

                                                <div className="itemContainer-base-price">
                                                    <div className="price-current">
                                                        <span className="rupee-symbol">₹</span>
                                                        {item.price?.toLocaleString('en-IN')}
                                                    </div>

                                                    <div className="price-details">
                                                        <span className="price-mrp">₹{item.mrp?.toLocaleString('en-IN')}</span>
                                                        <span className="price-discount">
                                                            {item.discount}% OFF
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="return-delivery-row">
                                                    <div className="returnPeriod-base-returnItem">
                                                        <div className="returnPeriod-base-returnIcon">
                                                            <img src={returnIcon} alt="Return" />
                                                        </div>
                                                        <div className="returnPeriod-base-returnText">
                                                            <span style={{marginLeft: '3px'}}>14 days </span>
                                                            return available
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Wishlist Action Buttons (loading + tick + toast: same pattern as home mob ProductCardCarouselStack / ProductSliderMulti) */}
                                                <div className="wishlist-action-buttons">
                                                    <button
                                                        type="button"
                                                        className={`wishlist-btn buy-now-btn ${!item.inStock ? "is-disabled" : ""}`}
                                                        disabled={!item.inStock}
                                                        onClick={() => handleBuyNow(item, index)}
                                                    >
                                                        Buy Now
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`wishlist-btn add-to-cart-btn ${
                                                            isAddToCartLoading ? "is-loading" : ""
                                                        } ${cartAddedByProductId[rowProductId] ? "is-added" : ""} ${!item.inStock ? "is-disabled" : ""}`}
                                                        disabled={!item.inStock || isAddToCartLoading}
                                                        aria-busy={isAddToCartLoading}
                                                        aria-label={
                                                            cartAddedByProductId[rowProductId]
                                                                ? "Added to cart"
                                                                : "Add to cart"
                                                        }
                                                        onClick={() => openSizePickerIfNeeded(item, index, "cart")}
                                                    >
                                                        {isAddToCartLoading ? (
                                                            <>
                                                                <span>Adding</span>
                                                                <span className="wishlist-cart-loader" aria-hidden />
                                                            </>
                                                        ) : cartAddedByProductId[rowProductId] ? (
                                                            <>
                                                                <IoCheckmark aria-hidden />
                                                                <span>Added</span>
                                                            </>
                                                        ) : (
                                                            "Add to Cart"
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                    {loadingMore ? (
                        <div
                            className="wishlist-mob-loading-more"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <div className="wishlist-mob-loading-more__spinner" />
                            <span className="wishlist-mob-loading-more__text">
                                Loading more…
                            </span>
                        </div>
                    ) : null}
                    {!noMoreItems && wishlistItems.length > 0 ? (
                        <div
                            ref={lastWishlistSentinelRef}
                            className="wishlist-mob-sentinel"
                            aria-hidden
                        />
                    ) : null}
                </div>
            </div>

            {/* Select size (toast-style bottom sheet) before add / buy */}
            {sizePicker && (
                <div
                    className="wishlist-size-sheet-overlay"
                    role="presentation"
                    onClick={() => setSizePicker(null)}
                >
                    <div
                        className="wishlist-size-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="wishlist-size-sheet-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="wishlist-size-sheet-handle" aria-hidden />
                        <h3 id="wishlist-size-sheet-title" className="wishlist-size-sheet-title">
                            Select size
                        </h3>
                        <p className="wishlist-size-sheet-product">{sizePicker.item.title}</p>
                        <div className="wishlist-size-chip-grid" role="group" aria-label="Sizes">
                            {sheetSizeOptions.map(({ key, available }) => (
                                <button
                                    key={key}
                                    type="button"
                                    disabled={!available}
                                    title={!available ? "Out of stock" : undefined}
                                    className={`wishlist-size-chip ${pendingSize === key ? "is-selected" : ""} ${!available ? "is-disabled" : ""}`}
                                    onClick={() => available && setPendingSize(key)}
                                    aria-pressed={pendingSize === key}
                                    aria-disabled={!available}
                                    aria-label={!available ? `${key}, out of stock` : key}
                                >
                                    {key}
                                </button>
                            ))}
                        </div>
                        {!sheetHasStock ? (
                            <p className="wishlist-size-sheet-oos">This product is out of stock in all sizes.</p>
                        ) : null}
                        <div className="wishlist-size-sheet-actions">
                            <button type="button" className="wishlist-size-sheet-btn wishlist-size-sheet-btn-secondary" onClick={() => setSizePicker(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="wishlist-size-sheet-btn wishlist-size-sheet-btn-primary"
                                disabled={pendingSize == null || !pendingOption?.available || !sheetHasStock}
                                onClick={confirmSizeSelection}
                            >
                                {sizePicker.intent === "buynow" ? "Buy now" : "Add to cart"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {buyNowPageLoading && <OverlayLoader />}

            {/* Remove Confirmation Modal */}
            {confirmRemoveIndex !== null && (
                <div className="remove-confirmation-modal">
                    <div className="remove-confirmation-content">
                        <h3>Remove Item?</h3>
                        <p>Are you sure you want to remove this item from your wishlist?</p>
                        <div className="remove-confirmation-buttons">
                            <button 
                                onClick={() => setConfirmRemoveIndex(null)}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => handleRemoveFromWishlist(confirmRemoveIndex)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
